import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Buildings, Plus, PencilSimple, Trash, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizationsPage = () => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchOrgs(); }, []);

  const fetchOrgs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/organizations`, { withCredentials: true });
      setOrgs(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openAdd = () => { setEditingOrg(null); setName(''); setShowModal(true); };
  const openEdit = (org) => { setEditingOrg(org); setName(org.name); setShowModal(true); };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editingOrg) {
        await axios.put(`${API_URL}/api/admin/organizations/${encodeURIComponent(editingOrg.name)}`, { name: name.trim() }, { withCredentials: true });
        toast.success(`Renamed to "${name.trim()}"`);
      } else {
        await axios.post(`${API_URL}/api/admin/organizations`, { name: name.trim() }, { withCredentials: true });
        toast.success(`Organization "${name.trim()}" created`);
      }
      setShowModal(false);
      fetchOrgs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (orgName) => {
    if (!window.confirm(`Delete "${orgName}"? This won't remove users — they'll just have no organization.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/organizations/${encodeURIComponent(orgName)}`, { withCredentials: true });
      toast.success(`"${orgName}" deleted`);
      fetchOrgs();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error deleting'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><SpinnerGap size={32} className="animate-spin text-blue-600" /></div>;

  return (
    <div className="space-y-6" data-testid="organizations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit'] tracking-tight">Organizations</h1>
          <p className="text-slate-500 mt-1">Manage company/organization names used across the portal</p>
        </div>
        <Button className="bg-blue-600 hover:bg-blue-700" onClick={openAdd} data-testid="add-org-btn">
          <Plus size={16} className="mr-2" /> Add Organization
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {orgs.map((org) => (
          <Card key={org.name} className="border-slate-200 hover:border-blue-300 transition-colors" data-testid={`org-card-${org.name}`}>
            <CardContent className="p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Buildings size={20} className="text-blue-700" />
                </div>
                <div>
                  <p className="font-semibold text-slate-900">{org.name}</p>
                  <p className="text-xs text-slate-400">{org.user_count || 0} users</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(org)} data-testid={`edit-org-${org.name}`}>
                  <PencilSimple size={14} />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => handleDelete(org.name)} data-testid={`delete-org-${org.name}`}>
                  <Trash size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {orgs.length === 0 && (
          <Card className="border-dashed border-slate-300 col-span-full">
            <CardContent className="py-12 text-center">
              <Buildings size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">No organizations yet. Add one to get started.</p>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOrg ? 'Rename Organization' : 'Add Organization'}</DialogTitle>
          </DialogHeader>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Organization name" data-testid="org-name-input"
                 onKeyDown={(e) => e.key === 'Enter' && handleSave()} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving || !name.trim()}>
              {saving && <SpinnerGap size={16} className="animate-spin mr-2" />}
              {editingOrg ? 'Rename' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrganizationsPage;
